import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { X, Plus, Edit2, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

export default function Announcements({ user, onClose }) {
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "" });

  useEffect(() => {
    loadAnnouncements();
    // Mark all as read when dialog opens
    markAsRead();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Neuerungen");
    }
  };

  const markAsRead = async () => {
    try {
      await axios.post(`${API}/announcements/mark-read`);
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error("Titel und Inhalt sind erforderlich");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/announcements/${editingId}`, formData);
        toast.success("Neuerung aktualisiert");
      } else {
        await axios.post(`${API}/announcements`, formData);
        toast.success("Neuerung erstellt");
      }
      
      setFormData({ title: "", content: "" });
      setShowForm(false);
      setEditingId(null);
      loadAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (announcement) => {
    setFormData({ title: announcement.title, content: announcement.content });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Neuerung wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/announcements/${id}`);
      toast.success("Neuerung gelöscht");
      loadAnnouncements();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: "", content: "" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
      <div className="rdr-card border-4 border-[#8b7355] w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold" style={{ color: '#3d2f1f', fontFamily: 'Crimson Text, serif' }}>
            Neuerungen
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
            title="Schließen"
          >
            <X size={24} style={{ color: '#3d2f1f' }} />
          </button>
        </div>

        {/* Admin Controls */}
        {user.is_admin && !showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="rdr-button mb-6"
          >
            <Plus size={16} className="mr-2" />
            Neue Neuerung
          </Button>
        )}

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 rounded" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#3d2f1f' }}>
              {editingId ? 'Neuerung bearbeiten' : 'Neue Neuerung'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Titel</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="rdr-input"
                  placeholder="z.B. Neue Features hinzugefügt"
                />
              </div>

              <div>
                <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>
                  Inhalt (Markdown)
                  <span className="text-xs ml-2" style={{ color: '#8b7355' }}>
                    **fett**, *kursiv*, [Link](url), - Liste
                  </span>
                </Label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="rdr-input w-full"
                  rows="8"
                  placeholder="Markdown-formatierter Inhalt..."
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="rdr-button">
                  {editingId ? 'Aktualisieren' : 'Erstellen'}
                </Button>
                <Button type="button" onClick={cancelForm} className="rdr-button" style={{ background: '#6d5838' }}>
                  Abbrechen
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Announcements List */}
        <div className="space-y-4">
          {announcements.length === 0 ? (
            <p className="text-center py-8" style={{ color: '#8b7355' }}>
              Noch keine Neuerungen vorhanden
            </p>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="p-4 rounded border-2 border-[#8b7355]"
                style={{ background: 'rgba(244, 232, 208, 0.3)' }}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-1" style={{ color: '#3d2f1f' }}>
                      {announcement.title}
                    </h3>
                    <p className="text-sm" style={{ color: '#8b7355' }}>
                      {announcement.created_by_display || announcement.created_by} • {' '}
                      {new Date(announcement.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  
                  {user.is_admin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                        title="Bearbeiten"
                      >
                        <Edit2 size={16} style={{ color: '#3d2f1f' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                        title="Löschen"
                      >
                        <Trash2 size={16} style={{ color: '#8b4513' }} />
                      </button>
                    </div>
                  )}
                </div>

                <div 
                  className="prose prose-sm max-w-none markdown-content" 
                  style={{ color: '#3d2f1f' }}
                >
                  <ReactMarkdown
                    components={{
                      ul: ({node, ...props}) => <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }} {...props} />,
                      ol: ({node, ...props}) => <ol style={{ listStyleType: 'decimal', paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }} {...props} />,
                      li: ({node, ...props}) => <li style={{ marginBottom: '4px' }} {...props} />,
                      p: ({node, ...props}) => <p style={{ marginBottom: '8px' }} {...props} />,
                      strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold', color: '#3d2f1f' }} {...props} />,
                      em: ({node, ...props}) => <em style={{ fontStyle: 'italic' }} {...props} />,
                      a: ({node, ...props}) => <a style={{ color: '#8b4513', textDecoration: 'underline' }} {...props} />
                    }}
                  >
                    {announcement.content}
                  </ReactMarkdown>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
